import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Flame, Phone } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

type SupportTier = "sponsor" | "home-group" | "crisis-resources";

interface SupportContext {
  tier: SupportTier;
  hasSponsor: boolean;
  hasHomeGroup: boolean;
}

export default function BurningDesireButton() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [showConfirm1, setShowConfirm1] = useState(false);
  const [showConfirm2, setShowConfirm2] = useState(false);
  const [showCrisisResources, setShowCrisisResources] = useState(false);
  const [cooldownText, setCooldownText] = useState("");

  const { data: supportContext, isLoading: isSupportContextLoading } = useQuery<SupportContext>({
    queryKey: ["/api/burning-desire/support-context"],
    enabled: !!user,
  });

  const { data: cooldown } = useQuery<{ onCooldown: boolean; expiresAt: string | null }>({
    queryKey: ["/api/burning-desire/cooldown"],
    enabled: !!user,
    refetchInterval: 60000,
  });

  const { data: activeSOS } = useQuery<any>({
    queryKey: ["/api/burning-desire/my-active"],
    enabled: !!user,
    refetchInterval: 30000,
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/burning-desire");
      return res.json();
    },
    onSuccess: async () => {
      const tier = supportContext?.tier;
      const description =
        tier === "sponsor"
          ? "Your sponsor and support network have been alerted."
          : "Your home group members have been alerted.";
      toast({
        title: "SOS Sent",
        description,
      });
      setShowConfirm2(false);
      queryClient.invalidateQueries({ queryKey: ["/api/burning-desire/cooldown"] });
      queryClient.invalidateQueries({ queryKey: ["/api/burning-desire/my-active"] });
      setLocation("/playbook?context=burning_desire");
    },
    onError: (error: any) => {
      toast({
        title: "Unable to send SOS",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
      setShowConfirm2(false);
    },
  });

  useEffect(() => {
    if (!cooldown?.expiresAt) {
      setCooldownText("");
      return;
    }
    const update = () => {
      const expires = new Date(cooldown.expiresAt!);
      if (expires <= new Date()) {
        setCooldownText("");
        queryClient.invalidateQueries({ queryKey: ["/api/burning-desire/cooldown"] });
        return;
      }
      setCooldownText(formatDistanceToNow(expires, { addSuffix: false }));
    };
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, [cooldown?.expiresAt]);

  if (!user) return null;

  const tier = supportContext?.tier;
  const isOnCooldown = cooldown?.onCooldown === true;
  const hasActiveSOS = !!activeSOS;
  const isDisabled = isOnCooldown || hasActiveSOS;

  const handleFirstClick = () => {
    if (isSupportContextLoading) {
      return;
    }

    if (!tier) {
      toast({
        title: "Loading",
        description: "Please wait while we determine your support network.",
      });
      return;
    }

    if (tier === "crisis-resources") {
      setShowCrisisResources(true);
      return;
    }

    if (isDisabled) {
      if (hasActiveSOS) {
        toast({
          title: "SOS Already Active",
          description: "Your SOS is active. Waiting for your support network to respond.",
        });
      } else if (isOnCooldown && cooldownText) {
        toast({
          title: "Cooldown Active",
          description: `You can send another SOS in ${cooldownText}.`,
        });
      }
      return;
    }
    setShowConfirm1(true);
  };

  const handleProceedToConfirm2 = () => {
    setShowConfirm1(false);
    setShowConfirm2(true);
  };

  const handleSend = () => {
    sendMutation.mutate();
  };

  const confirm1Description =
    tier === "sponsor"
      ? "This will send an urgent alert to your sponsor, your sponsor-siblings, and your home group members. Your sponsor will need to respond with a written note before the alert is resolved."
      : tier === "home-group"
      ? "This will send an urgent alert to your home group members, letting them know you need support right now."
      : "";

  const confirm2Description =
    tier === "sponsor"
      ? "This is the final step. Once you confirm, your sponsor, sponsor-siblings, and home group members will be immediately notified. This cannot be undone."
      : tier === "home-group"
      ? "This is the final step. Once you confirm, your home group members will be immediately notified. This cannot be undone."
      : "";

  return (
    <>
      <div
        className="fixed z-50 flex flex-col items-center gap-1 bottom-28 md:bottom-6"
        style={{
          left: "max(1.5rem, env(safe-area-inset-left))",
        }}
      >
        {isOnCooldown && cooldownText && tier && tier !== "crisis-resources" && (
          <span
            className="text-[10px] font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full whitespace-nowrap"
            data-testid="text-cooldown-timer"
          >
            {cooldownText}
          </span>
        )}
        <button
          onClick={handleFirstClick}
          className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all ${
            !tier
              ? "bg-muted opacity-60"
              : hasActiveSOS && tier !== "crisis-resources"
              ? "bg-orange-500 dark:bg-orange-600 animate-pulse"
              : isOnCooldown && tier !== "crisis-resources"
              ? "bg-muted cursor-not-allowed opacity-60"
              : "bg-red-600 dark:bg-red-700"
          }`}
          style={{ boxShadow: hasActiveSOS && tier && tier !== "crisis-resources" ? "0 0 20px rgba(249, 115, 22, 0.5)" : undefined }}
          data-testid="button-burning-desire"
          aria-label="Burning Desire SOS"
        >
          <Flame className={`w-7 h-7 ${(!tier || (isOnCooldown && tier !== "crisis-resources")) ? "text-muted-foreground" : "text-white"}`} />
        </button>
      </div>

      <Dialog open={showConfirm1} onOpenChange={setShowConfirm1}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-red-500" />
              Burning Desire SOS
            </DialogTitle>
            <DialogDescription>
              {confirm1Description}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-muted-foreground space-y-2 py-2">
            <p>After sending, there is a 6-hour cooldown before you can send another SOS.</p>
            <p>Are you sure you want to proceed?</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirm1(false)} data-testid="button-cancel-sos-1">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleProceedToConfirm2} data-testid="button-proceed-sos">
              Yes, Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showConfirm2} onOpenChange={setShowConfirm2}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-red-500" />
              Final Confirmation
            </DialogTitle>
            <DialogDescription>
              {confirm2Description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirm2(false)} data-testid="button-cancel-sos-2">
              Go Back
            </Button>
            <Button
              variant="destructive"
              onClick={handleSend}
              disabled={sendMutation.isPending}
              data-testid="button-confirm-send-sos"
            >
              {sendMutation.isPending ? "Sending..." : "Send SOS Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCrisisResources} onOpenChange={() => {}}>
        <DialogContent
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-red-500" />
              Crisis Resources
            </DialogTitle>
            <DialogDescription>
              You don't currently have a sponsor or home group connected. Please reach out to one of these crisis resources for immediate support.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 rounded-md border">
              <Phone className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-sm" data-testid="text-cma-hotline-title">CMA Crisis Hotline</p>
                <p className="text-xs text-muted-foreground mb-1">Crystal Meth Anonymous official crisis line for immediate peer support.</p>
                <a
                  href="tel:1-855-638-4373"
                  className="text-sm font-medium text-primary underline"
                  data-testid="link-cma-hotline"
                >
                  1-855-METH-FREE (1-855-638-4373)
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-md border">
              <Phone className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-sm" data-testid="text-988-title">Suicide &amp; Crisis Lifeline</p>
                <p className="text-xs text-muted-foreground mb-1">The U.S. National Suicide &amp; Crisis Lifeline provides free, confidential support 24/7.</p>
                <a
                  href="tel:988"
                  className="text-sm font-medium text-primary underline"
                  data-testid="link-988-lifeline"
                >
                  988
                </a>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCrisisResources(false)}
              className="w-full"
              data-testid="button-close-crisis-resources"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
